import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useDispatch } from 'react-redux';

import CustomConfirm from '../../components/customConfirm';
import EmptyState from '../../components/emptyState';
import Loading from '../../components/loading';
import Table from '../../components/table';
import ToolBar from '../../components/toolbar';
import { setAlert } from '../../redux/dashboard/actions';
import API from '../../services/api';
import utils from '../../utils';
import ManageWebEmbedModal from './modals/manageWebEmbed';
import WebEmbedWizard from './steps/webEmbedWizard';
import createWebEmbedColumns from './webEmbedColumns';
import {
  DEFAULT_ATTRIBUTION_WINDOWS,
  createEmptyWebEmbed,
  getWebPropertiesFromResponse,
  getWebPropertyFromResponse,
  getWebPropertyPayload,
  isVisibleWebEmbed,
  normalizeAttributionWindows,
  normalizeOption,
  normalizeWebEmbed
} from './webEmbedUtils';

import './styles.scss';

const WebAnalyticsContainer = ({ lastUpdated }) => {
  const [webEmbeds, setWebEmbeds] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [itemsCount, setItemsCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectorLoading, setSelectorLoading] = useState(false);
  const [selectorError, setSelectorError] = useState('');
  const [activationError, setActivationError] = useState('');
  const [searchingBy, setSearchingBy] = useState('');
  const [createMode, setCreateMode] = useState(false);
  const [attributionWindows, setAttributionWindows] = useState(DEFAULT_ATTRIBUTION_WINDOWS);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [campaignTotalSize, setCampaignTotalSize] = useState(0);
  const [newWebEmbed, setNewWebEmbed] = useState(createEmptyWebEmbed(DEFAULT_ATTRIBUTION_WINDOWS));
  const [selectedWebEmbed, setSelectedWebEmbed] = useState(null);
  const [webEmbedToDelete, setWebEmbedToDelete] = useState(null);
  const [portal, setPortal] = useState(null);

  const fetchIdRef = useRef(0);
  const campaignFilterRef = useRef('');
  const dispatch = useDispatch();

  const showAlert = useCallback(
    (message, type) => dispatch(setAlert(utils.generateAlert(message, type))),
    [dispatch]
  );

  useEffect(() => {
    let isMounted = true;

    API.getPortals()
      .then(response => {
        if (!isMounted) return;
        setPortal(response?.brandPortals?.[0] || null);
      })
      .catch(error => {
        if (!isMounted) return;
        setPortal(null);
        showAlert('Failed to load portal. Try again later.', 'error');
        console.error('Error fetching portals:', error);
      })
      .finally(() => {
        if (isMounted) setPortalLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [showAlert]);

  const loadWebEmbeds = useCallback(
    (silent = false) => {
      if (!portal?.id) {
        setWebEmbeds([]);
        setLoading(false);
        return Promise.resolve([]);
      }

      if (!silent) setLoading(true);
      return API.getWebProperties(portal.id)
        .then(response => {
          const supportedWindows = normalizeAttributionWindows(response);
          const loadedWebEmbeds = getWebPropertiesFromResponse(response)
            .filter(isVisibleWebEmbed)
            .map(webEmbed => normalizeWebEmbed(webEmbed, supportedWindows));
          setAttributionWindows(supportedWindows);
          setWebEmbeds(loadedWebEmbeds);
          setSelectedWebEmbed(current =>
            current ? loadedWebEmbeds.find(webEmbed => webEmbed.id === current.id) || null : null
          );
          setNewWebEmbed(current =>
            current.id ? loadedWebEmbeds.find(webEmbed => webEmbed.id === current.id) || current : current
          );
          return loadedWebEmbeds;
        })
        .catch(error => {
          showAlert('Failed to load Web Embeds. Try again later.', 'error');
          console.error('Error loading Web Embeds:', error);
          return [];
        })
        .finally(() => {
          if (!silent) setLoading(false);
        });
    },
    [portal?.id, showAlert]
  );

  const loadSelectorData = useCallback((nextPageToken = '', searchingBy = '') => {
    if (!portal?.companyId) {
      setCampaignOptions([]);
      setCampaignTotalSize(0);
      setSelectorError('Select a portal with a company before configuring attribution.');
      return Promise.resolve('');
    }

    setSelectorLoading(true);
    setSelectorError('');
    const searchTerm = searchingBy.trim();
    const searchFilter = searchTerm
      ? ` AND (displayName ILIKE "%${searchTerm}%" OR id ILIKE "%${searchTerm}%")`
      : '';
    const filter = encodeURI(`brandPortalId = "${portal.id}"${searchFilter}`);

    if (filter !== campaignFilterRef.current) {
      campaignFilterRef.current = filter;
      setCampaignOptions([]);
      setCampaignTotalSize(0);
    }

    return API.getCampaigns(portal.companyId, nextPageToken, filter, 40, 'displayName, id')
      .then(response => {
        if (campaignFilterRef.current !== filter) return '';

        const campaigns = (response.campaigns || []).map(normalizeOption);
        setCampaignTotalSize(response.totalSize ?? campaigns.length);
        setCampaignOptions(current =>
          nextPageToken ? utils.mergeArraysByField(campaigns, current, 'value') : campaigns
        );
        return response.nextPageToken || '';
      })
      .catch(error => {
        if (campaignFilterRef.current !== filter) return '';
        setSelectorError('Campaigns could not be loaded. Retry without losing your entries.');
        console.error('Error loading Web Embed attribution data:', error);
        return '';
      })
      .finally(() => setSelectorLoading(false));
  }, [portal?.companyId, portal?.id]);

  useEffect(() => {
    if (portalLoading) return;
    setSearchingBy('');
    setCreateMode(false);
    setNewWebEmbed(createEmptyWebEmbed(DEFAULT_ATTRIBUTION_WINDOWS));
    loadWebEmbeds();
    loadSelectorData();
  }, [lastUpdated, loadSelectorData, loadWebEmbeds, portalLoading]);

  const hasPendingWebEmbeds = webEmbeds.some(webEmbed => webEmbed.connectionState !== 'active');
  useEffect(() => {
    if (!portal?.id || !hasPendingWebEmbeds) return undefined;
    const pollingId = window.setInterval(() => loadWebEmbeds(true), 15000);
    return () => window.clearInterval(pollingId);
  }, [hasPendingWebEmbeds, loadWebEmbeds, portal?.id]);

  const handleSearch = useDebouncedCallback(term => setSearchingBy(term), 300);

  const tableColumns = useMemo(() => createWebEmbedColumns(), []);
  const fetchData = useCallback(
    ({ pageIndex, pageSize, sortBy }) => {
      const fetchId = ++fetchIdRef.current;
      const term = searchingBy.toLowerCase().trim();
      const filteredWebEmbeds = webEmbeds.filter(webEmbed => webEmbed.name.toLowerCase().includes(term));
      const sortConfig = utils.generateSortByConfig(sortBy[0], { id: 'displayName', type: 'string' }, tableColumns);
      const sortedWebEmbeds = utils.sort([...filteredWebEmbeds], sortConfig);

      if (fetchId === fetchIdRef.current) {
        const startRow = pageSize * pageIndex;
        setTableData(sortedWebEmbeds.slice(startRow, startRow + pageSize));
        setItemsCount(sortedWebEmbeds.length);
        setPageCount(Math.ceil(sortedWebEmbeds.length / pageSize));
      }
    },
    [searchingBy, tableColumns, webEmbeds]
  );

  const activateWebEmbed = () => {
    if (!portal?.id) return Promise.resolve(false);
    setSaving(true);
    setActivationError('');

    return API.createWebProperty(portal.id, getWebPropertyPayload(newWebEmbed, portal.id))
      .then(response => {
        const persistedWebEmbed = normalizeWebEmbed(getWebPropertyFromResponse(response), attributionWindows);
        if (!persistedWebEmbed.id) throw new Error('The Web Embed API did not return the persisted resource.');
        setNewWebEmbed(persistedWebEmbed);
        setWebEmbeds(current => [persistedWebEmbed, ...current.filter(item => item.id !== persistedWebEmbed.id)]);
        showAlert('Web Embed activated. Waiting for tracking data.', 'success');
        return true;
      })
      .catch(error => {
        const message = error?.message || 'Activation failed. Check the fields above and try again.';
        setActivationError(`${message} Your entries have been preserved.`);
        showAlert(message, 'error');
        console.error('Error activating Web Embed:', error);
        return false;
      })
      .finally(() => setSaving(false));
  };

  const finishCreate = () => {
    setCreateMode(false);
    setActivationError('');
    setNewWebEmbed(createEmptyWebEmbed(attributionWindows));
    return loadWebEmbeds();
  };

  const updateWebEmbed = (webEmbedDraft, updateMask) => {
    if (!portal?.id || !updateMask.length) return;
    setSaving(true);
    return API.updateWebProperty(
      portal.id,
      webEmbedDraft.id,
      getWebPropertyPayload(webEmbedDraft, portal.id),
      updateMask
    )
      .then(() => {
        showAlert('Web Embed updated successfully!', 'success');
        setSelectedWebEmbed(null);
        return loadWebEmbeds();
      })
      .catch(error => {
        showAlert('Failed to update Web Embed. Try again later.', 'error');
        console.error('Error updating Web Embed:', error);
      })
      .finally(() => setSaving(false));
  };

  const deleteWebEmbed = () => {
    if (!portal?.id || !webEmbedToDelete) return;
    setSaving(true);
    return API.deleteWebProperty(portal.id, webEmbedToDelete.id)
      .then(() => {
        setWebEmbeds(current => current.filter(webEmbed => webEmbed.id !== webEmbedToDelete.id));
        setSelectedWebEmbed(null);
        setWebEmbedToDelete(null);
        showAlert('Web Embed deleted successfully!', 'success');
      })
      .catch(error => {
        showAlert('Failed to delete Web Embed. Try again later.', 'error');
        console.error('Error deleting Web Embed:', error);
      })
      .finally(() => setSaving(false));
  };

  const openCreateMode = () => {
    setActivationError('');
    setNewWebEmbed(createEmptyWebEmbed(attributionWindows));
    setCreateMode(true);
  };

  if (portalLoading) {
    return <Loading size={15} />;
  }

  if (!portal) {
    return (
      <EmptyState
        icon="portal"
        title="No Portals found. Create your first Portal."
        details="If you do have created portals, please contact support."
      />
    );
  }

  return (
    <div className="web-analytics">
      {createMode ? (
        <WebEmbedWizard
          webEmbed={newWebEmbed}
          onChange={setNewWebEmbed}
          onActivate={activateWebEmbed}
          onSubmit={finishCreate}
          onCancel={() => setCreateMode(false)}
          attributionWindows={attributionWindows}
          campaignOptions={campaignOptions}
          campaignTotalSize={campaignTotalSize}
          onLoadCampaigns={loadSelectorData}
          selectorLoading={selectorLoading}
          selectorError={selectorError}
          activationError={activationError}
          onRetrySelectors={loadSelectorData}
        />
      ) : (
        <>
          <ToolBar
            title="Web Embeds"
            icon="keyword"
            count={itemsCount}
            searchPlaceholder="Search by web embed name"
            onSearch={handleSearch}
            searchingBy={searchingBy}
            hasButton
            buttonTitle="Create Web Embed"
            buttonPress={openCreateMode}
            disabledButton={!portal?.id || portalLoading || loading}
          />
          <div className="web-analytics-container">
            <div className="d-flex flex-fill">
              <div id="web-embeds-table">
                <Table
                  columns={tableColumns}
                  data={tableData}
                  dataLabel="web embeds"
                  fetchData={fetchData}
                  loading={portalLoading || loading}
                  pageCount={pageCount}
                  itemsCount={itemsCount}
                  searchBy={searchingBy}
                  initialSortColumn="displayName"
                  hasInteractiveRows
                  isItemDetailsClosed={!selectedWebEmbed}
                  onItemSelected={setSelectedWebEmbed}
                  resetOnChange={portal?.id}
                />
              </div>
            </div>
            <ManageWebEmbedModal
              webEmbed={selectedWebEmbed}
              isOpen={!!selectedWebEmbed}
              onCancel={() => setSelectedWebEmbed(null)}
              onConfirm={updateWebEmbed}
              onDelete={setWebEmbedToDelete}
              isLoading={saving}
              attributionWindows={attributionWindows}
              campaignOptions={campaignOptions}
              campaignTotalSize={campaignTotalSize}
              onLoadCampaigns={loadSelectorData}
              selectorLoading={selectorLoading}
              selectorError={selectorError}
              onRetrySelectors={loadSelectorData}
            />
            <CustomConfirm
              isOpen={!!webEmbedToDelete}
              messageType="deleteWebEmbed"
              messageContext={webEmbedToDelete?.name}
              cancelCb={() => setWebEmbedToDelete(null)}
              confirmCb={deleteWebEmbed}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default WebAnalyticsContainer;
